import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import AnimatedSection from '../components/AnimatedSection';
import { siteInfo, phoneToTelHref, mapsHref } from '../data/siteInfo';

const Contact = () => {
  const { contact, companyName, openingHours } = siteInfo;
  const telHref = phoneToTelHref(contact.phone);
  const officeHours = openingHours.map((h) => `${h.day}: ${h.hours}`).join('\n');

  const contactItems = useMemo(
    () => [
      contact.fullAddress && { icon: '📍', label: 'Address', value: contact.fullAddress },
      contact.phone && { icon: '📞', label: 'Phone', value: contact.phone, href: telHref || undefined },
      { icon: '✉️', label: 'Email', value: contact.email, href: `mailto:${contact.email}` },
      { icon: '🕐', label: 'Office Hours', value: officeHours }
    ].filter(Boolean),
    [contact.fullAddress, contact.phone, contact.email, telHref, officeHours]
  );

  const faqs = useMemo(
    () => [
      {
        q: 'Where are you based?',
        a: 'Easy Moove Ltd is based in Preston, United Kingdom. We invest in property through buying, selling and letting opportunities, with a focus on long-term value.'
      },
      {
        q: 'What do you focus on?',
        a: 'We focus on property investment and portfolio building, buying and selling property, residential and commercial opportunities, letting and rental investments, and supporting other investors with property transactions.'
      },
      {
        q: 'Do you work with other investors?',
        a: 'Yes. We help other property investors identify opportunities to buy and sell property and support them throughout the investment process — whether building a portfolio, selling an existing property, or exploring new opportunities.'
      },
      {
        q: 'How quickly will you respond to my enquiry?',
        a: `We aim to respond to all enquiries within one working day. Email us at ${siteInfo.contact.email} or use the form on this page.`
      }
    ],
    []
  );

  const mapShortAddress = useMemo(() => {
    if (!contact.fullAddress) return '';
    const parts = contact.fullAddress.split(',').map((s) => s.trim()).filter(Boolean);
    return parts.length >= 2 ? `${parts[0]}, ${parts[1]}` : contact.fullAddress;
  }, [contact.fullAddress]);

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name required';
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Valid email required';
    if (!form.message.trim()) e.message = 'Message required';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    // No backend: open the visitor's email client pre-filled with their enquiry.
    const subject = form.subject || 'Website Enquiry';
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : '',
      '',
      form.message
    ]
      .filter(Boolean)
      .join('\n');

    const mailto = `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title={`Contact ${companyName} | Preston Property Investment`}
        description={`Contact ${companyName} in Preston about property investment, buying, selling or letting. Email ${contact.email}.`}
      />

      <section className="contact-page-hero">
        <div className="contact-page-hero__bg" aria-hidden="true">
          <img src="https://picsum.photos/seed/contact-hero/1920/600" alt="" className="contact-page-hero__img" />
          <div className="contact-page-hero__overlay" />
        </div>
        <div className="container contact-page-hero__inner">
          <AnimatedSection variant="fadeUp">
            <p className="contact-page-hero__eyebrow">Based in Preston</p>
            <h1 className="contact-page-hero__title">Get in Touch</h1>
            <p className="contact-page-hero__lead">
              Looking to invest, buy, sell or explore a new opportunity? Send us a message and we will get back to you.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="contact-page-main">
        <div className="container contact-page-container">
          <div className="contact-layout">
            <div className="contact-layout__form">
              <AnimatedSection variant="fadeLeft">
                <div className="contact-form-card">
                  <h2 className="contact-form-card__title">Send Us a Message</h2>
                  <p className="contact-form-card__intro">
                    Fill in the form and your email app will open with the details ready to send.
                  </p>

                  <AnimatePresence mode="wait">
                    {submitted ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="contact-form-success"
                      >
                        <div className="contact-form-success__icon" aria-hidden="true">✅</div>
                        <h3 className="contact-form-success__title">Almost there!</h3>
                        <p className="contact-form-success__text">
                          Thanks, {form.name}. Your email app should have opened — just hit send and we&apos;ll be in touch.
                          If nothing opened, please email us directly at{' '}
                          <a href={`mailto:${contact.email}`}>{contact.email}</a>.
                        </p>
                      </motion.div>
                    ) : (
                      <motion.form key="form" onSubmit={handleSubmit} className="contact-form" noValidate>
                        <div className="contact-form-grid">
                          <div className="form-group">
                            <label className="form-label" htmlFor="contact-name">Your Name *</label>
                            <input
                              id="contact-name"
                              className={`form-input ${errors.name ? 'error' : ''}`}
                              type="text"
                              value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                              placeholder="John Smith"
                              autoComplete="name"
                            />
                            {errors.name && <span className="form-error">{errors.name}</span>}
                          </div>
                          <div className="form-group">
                            <label className="form-label" htmlFor="contact-email">Email Address *</label>
                            <input
                              id="contact-email"
                              className={`form-input ${errors.email ? 'error' : ''}`}
                              type="email"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              placeholder="john@example.com"
                              autoComplete="email"
                            />
                            {errors.email && <span className="form-error">{errors.email}</span>}
                          </div>
                          <div className="form-group">
                            <label className="form-label" htmlFor="contact-phone">Phone Number</label>
                            <input
                              id="contact-phone"
                              className="form-input"
                              type="tel"
                              value={form.phone}
                              onChange={(e) => setForm({ ...form, phone: e.target.value })}
                              placeholder="07700 000000"
                              autoComplete="tel"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label" htmlFor="contact-subject">Subject</label>
                            <select
                              id="contact-subject"
                              className="form-input"
                              value={form.subject}
                              onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            >
                              <option value="">Select subject...</option>
                              {['General Enquiry', 'Buy / Investment Opportunity', 'Sell a Property', 'Portfolio Support', 'Other'].map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group contact-form-grid__full">
                            <label className="form-label" htmlFor="contact-message">Message *</label>
                            <textarea
                              id="contact-message"
                              className={`form-input ${errors.message ? 'error' : ''}`}
                              rows={6}
                              value={form.message}
                              onChange={(e) => setForm({ ...form, message: e.target.value })}
                              placeholder="Tell us how we can help..."
                            />
                            {errors.message && <span className="form-error">{errors.message}</span>}
                          </div>
                        </div>
                        <motion.button
                          type="submit"
                          className="contact-form-submit"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Send Message
                        </motion.button>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </AnimatedSection>
            </div>

            <aside className="contact-layout__aside" aria-label="Contact details and map">
              <AnimatedSection variant="fadeRight">
                <div className="contact-aside-stack">
                  <div className="contact-info-card">
                    <h3 className="contact-info-card__title">Contact Details</h3>
                    <ul className="contact-info-list">
                      {contactItems.map((item) => (
                        <li key={item.label} className="contact-info-list__item">
                          <span className="contact-info-list__icon" aria-hidden="true">{item.icon}</span>
                          <div className="contact-info-list__body">
                            <div className="contact-info-list__label">{item.label}</div>
                            {item.href ? (
                              <a href={item.href} className="contact-info-list__value contact-info-list__value--link">{item.value}</a>
                            ) : (
                              <p className="contact-info-list__value" style={{ whiteSpace: 'pre-line' }}>{item.value}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {contact.fullAddress && (
                    <div className="contact-map-card">
                      <div className="contact-map-card__placeholder">
                        <span className="contact-map-card__pin" aria-hidden="true">🗺️</span>
                        <p className="contact-map-card__address">{mapShortAddress}</p>
                        <a
                          href={mapsHref(contact.fullAddress)}
                          className="contact-map-card__link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on Google Maps
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </AnimatedSection>
            </aside>
          </div>
        </div>
      </section>

      <section className="contact-faq-section">
        <div className="container contact-faq-container">
          <AnimatedSection variant="fadeUp" className="contact-faq-intro">
            <h2 className="section-heading gold-underline">Frequently Asked Questions</h2>
          </AnimatedSection>
          <div className="contact-faq-list">
            {faqs.map((faq, i) => (
              <AnimatedSection key={faq.q} variant="fadeUp" delay={i * 0.08}>
                <div className="contact-faq-item">
                  <button
                    type="button"
                    className="contact-faq-item__trigger"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    id={`contact-faq-${i}`}
                    aria-controls={`contact-faq-panel-${i}`}
                  >
                    <span className="contact-faq-item__q">{faq.q}</span>
                    <motion.span
                      className="contact-faq-item__chev"
                      animate={{ rotate: openFaq === i ? 180 : 0 }}
                      aria-hidden="true"
                    >
                      ▼
                    </motion.span>
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        id={`contact-faq-panel-${i}`}
                        role="region"
                        aria-labelledby={`contact-faq-${i}`}
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="contact-faq-item__panel-wrap"
                      >
                        <div className="contact-faq-item__panel">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
